import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { DietType } from '../types';
import { calculatePlan, CalculationPlanResponse } from '../api/calculate';
import { isOutOfStockError } from '../api/client';
import { buildApiPayload } from '../utils/formPayload';
import SelectableCard from '../components/funnel/SelectableCard';
import StepNav from '../components/funnel/StepNav';
import { Phase2Header } from '../components/funnel/Phase2Header';
import FunnelStickyCTA from '../components/funnel/FunnelStickyCTA';
import { BreedPhoto } from '../components/funnel/BreedPhoto';
import { formatNumber } from '../utils/formatters';

export default function Step11TuPlan() {
  const { formData, updateFormData, goToNextStep, setCurrentStep } = useForm();

  const { t, config, refreshConfig } = useAppConfig();

  // Fracción de la ración que cubre la dieta MIXTA (la otra mitad la pone el
  // tutor con su comida habitual). Valor de negocio configurable en el backend
  // (pricing_rules.mixta_diet_fraction); fallback 0.5 si no viene en config.
  const mixtaDietFraction =
    typeof config.pricing_rules.mixta_diet_fraction === 'number' && config.pricing_rules.mixta_diet_fraction > 0
      ? config.pricing_rules.mixta_diet_fraction
      : 0.5;

  // El plan completo (DER + gramos) viene del backend para la dieta COMPLETA
  // (ración 100%). La dieta MIXTA se deriva multiplicando por mixtaDietFraction.
  const [plan, setPlan] = useState<CalculationPlanResponse | null>(null);
  const [previewError, setPreviewError] = useState(false);

  const previewKey = useMemo(
    () =>
      JSON.stringify([
        formData.peso,
        formData.ageCategory,
        formData.activityLevel,
        formData.bodyCondition,
        formData.isNeutered,
        formData.ageYears,
        formData.ageMonths,
        formData.ageTotalMonths,
        formData.selectedRecipes,
      ]),
    [
      formData.peso,
      formData.ageCategory,
      formData.activityLevel,
      formData.bodyCondition,
      formData.isNeutered,
      formData.ageYears,
      formData.ageMonths,
      formData.ageTotalMonths,
      formData.selectedRecipes,
    ],
  );

  const previewSeqRef = useRef(0);

  const fetchPlan = useCallback(() => {
    const seq = ++previewSeqRef.current;
    setPlan(null);
    setPreviewError(false);
    // Pedimos siempre el plan COMPLETA (ración 100%) para tener la base real
    // de gramos/kcal del backend; la MIXTA se deriva con mixtaDietFraction.
    calculatePlan(buildApiPayload(formData, { dietType: DietType.COMPLETA, platform: config.effective_platform }))
      .then((result) => {
        if (seq !== previewSeqRef.current) return;
        if (result && typeof result.der === 'number' && result.der > 0) {
          setPlan(result);
        } else {
          setPreviewError(true);
        }
      })
      .catch((err: unknown) => {
        if (seq !== previewSeqRef.current) return;
        // Sin stock en TODAS las tallas de una receta: no es reintentable (reintentar
        // reenvía la misma selección → mismo 409). La quitamos, marcamos el rebote y
        // volvemos al selector (Step10), que avisa y re-hidrata — como checkout/Mi Cuenta.
        if (isOutOfStockError(err)) {
          const unavailable = new Set(err.unavailableProductIds);
          const remaining = (formData.selectedRecipes ?? []).filter((id) => !unavailable.has(id));
          updateFormData({ selectedRecipes: remaining, recipeStockBounced: true });
          Promise.resolve(refreshConfig({ silent: true }))
            .catch(() => undefined)
            .finally(() => {
              if (seq === previewSeqRef.current) setCurrentStep(10);
            });
          return;
        }
        setPreviewError(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  useEffect(() => {
    fetchPlan();
    return () => {
      // Invalida cualquier respuesta en vuelo de un previewKey anterior.
      previewSeqRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey]);

  // DER viene únicamente del backend — sin fallback aproximado.
  const dailyKcal = plan ? plan.der : null;
  const previewLoading = dailyKcal === null && !previewError;

  // Persist the backend DER into formData so Step10's stock heads-up can estimate
  // the plan's cans when the user navigates back. This is the only writer of
  // dailyKcal in the funnel and only that heads-up reads it.
  useEffect(() => {
    if (dailyKcal == null || dailyKcal <= 0) return;
    const rounded = Math.round(dailyKcal);
    if (formData.dailyKcal !== rounded) updateFormData({ dailyKcal: rounded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyKcal]);
  const selectedDietType: DietType = formData.dietType || DietType.COMPLETA;

  const dietPreviews = useMemo(() => {
    if (!plan) return null;
    const completaGrams = plan.daily_total_grams;
    return {
      completa: {
        dailyKcal: Math.round(plan.der),
        dailyGrams: Math.round(completaGrams),
        coveragePct: 100,
      },
      mixta: {
        dailyKcal: Math.round(plan.der * mixtaDietFraction),
        dailyGrams: Math.round(completaGrams * mixtaDietFraction),
        coveragePct: Math.round(mixtaDietFraction * 100),
      },
    };
  }, [plan, mixtaDietFraction]);

  function handleContinue() {
    if (previewError) {
      fetchPlan();
      return;
    }
    updateFormData({ dietType: selectedDietType });
    goToNextStep();
  }

  const nombre = formData.nombre || 'tu perro';
  // El CTA solo se deshabilita mientras carga. En error pasa a ser "Reintentar"
  // (CTA habilitado) — patrón unificado con Step12.
  const ctaDisabled = previewLoading;

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      <Phase2Header step={2} />
      <StepNav
        currentStep={1}
        completedSteps={[
          0,
          ...(formData.dietType ? [1] : []),
          ...(Array.isArray(formData.selectedSupplements) ? [2] : []),
          ...(formData.email ? [3] : []),
        ]}
        onStepClick={(stepIndex) => {
          const targetStep: Record<number, number> = { 0: 10, 2: 13, 3: 14 };
          if (targetStep[stepIndex] !== undefined) setCurrentStep(targetStep[stepIndex]);
        }}
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 funnel-bottom-clearance lg:pb-28" data-funnel-scroll>
        <div className="max-w-3xl mx-auto space-y-7">
          <div className="flex items-center gap-4 text-left sm:block sm:text-center">
            <div className="flex w-[22%] max-w-[76px] flex-shrink-0 justify-center sm:mx-auto sm:mb-3 sm:w-auto sm:max-w-none">
              <BreedPhoto
                breedKey={formData.raza}
                alt={nombre}
                className="h-16 w-16 drop-shadow-md select-none sm:h-20 sm:w-20"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-fredoka text-xl font-semibold leading-tight text-[#1a1a1a] sm:text-2xl lg:text-3xl">
                {t('step11v2.title_diet', '¿Qué tipo de dieta quieres para {nombre}?', {
                  nombre,
                })}
              </h1>
              {previewLoading && (
                <p
                  role="status"
                  aria-live="polite"
                  className="mt-2 font-fredoka text-base font-bold text-[#008BBC] sm:mt-3 sm:text-xl"
                >
                  {t('step11v2.calculating', 'Calculando las necesidades de {nombre}…', { nombre })}
                </p>
              )}
              {previewError && (
                <p role="alert" className="mt-2 font-fredoka text-base font-bold text-red-600 sm:mt-3 sm:text-xl">
                  {t(
                    'step11v2.preview_error',
                    'No se pudo calcular el plan de {nombre}. Toca «Reintentar» para volver a intentarlo.',
                    { nombre },
                  )}
                </p>
              )}
              {dailyKcal !== null && (
                <p className="mt-2 font-fredoka text-base font-bold text-[#008BBC] sm:mt-3 sm:text-xl">
                  {t('step11v2.needs', '{nombre} necesita ~{kcal} kcal al día', {
                    nombre,
                    kcal: formatNumber(Math.round(dailyKcal)),
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SelectableCard
              selected={selectedDietType === DietType.COMPLETA}
              onClick={() => updateFormData({ dietType: DietType.COMPLETA })}
              badge={t('step11v2.badge.recommended', 'Recomendado')}
            >
              <div className="space-y-3 text-left sm:text-center">
                <div className="flex items-start gap-3 sm:flex-col sm:items-center">
                  <img
                    src="/images/funnel/bowl-natural.png"
                    alt={t('step11v2.completa.title', 'Dieta completa')}
                    className="h-20 w-20 flex-shrink-0 object-contain sm:mb-2 sm:h-16 sm:w-16"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-fredoka text-lg font-bold leading-tight text-[#1a1a1a] sm:text-base">
                      {t('step11v2.completa.title', 'Dieta completa')}
                    </p>
                    <p className="mt-1 font-poppins text-base leading-snug text-black/60 sm:min-h-[2.5rem] sm:text-sm">
                      <span className="sm:hidden">
                        {t('step11v2.completa.desc_mobile', 'Milo y Lola cubre toda la ración diaria de {nombre}.', {
                          nombre,
                        })}
                      </span>
                      <span className="hidden sm:inline">
                        {t('step11v2.completa.desc', 'Milo y Lola cubre toda la ración diaria de {nombre}.', {
                          nombre,
                        })}
                      </span>
                    </p>
                  </div>
                </div>
                {dietPreviews && (
                  <div className="grid w-full grid-cols-3 gap-2 rounded-xl bg-[#FAF7F1] p-3 sm:block sm:space-y-1.5">
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.coverage', 'Ración')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">{dietPreviews.completa.coveragePct}%</span>
                    </div>
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.grams', 'Gramos/día')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">
                        {formatNumber(dietPreviews.completa.dailyGrams)}g
                      </span>
                    </div>
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.kcal', 'Kcal/día')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">
                        {formatNumber(dietPreviews.completa.dailyKcal)} kcal
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </SelectableCard>

            <SelectableCard
              selected={selectedDietType === DietType.MIXTA}
              onClick={() => updateFormData({ dietType: DietType.MIXTA })}
            >
              <div className="space-y-3 text-left sm:text-center">
                <div className="flex items-start gap-3 sm:flex-col sm:items-center">
                  <img
                    src="/images/funnel/bowl-mixta.png"
                    alt={t('step11v2.mixta.title', 'Dieta mixta')}
                    className="h-20 w-20 flex-shrink-0 object-contain sm:mb-2 sm:h-16 sm:w-16"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-fredoka text-lg font-bold leading-tight text-[#1a1a1a] sm:text-base">
                      {t('step11v2.mixta.title', 'Dieta mixta')}
                    </p>
                    <p className="mt-1 font-poppins text-base leading-snug text-black/60 sm:min-h-[2.5rem] sm:text-sm">
                      <span className="sm:hidden">
                        {t('step11v2.mixta.desc_mobile', 'Mitad Milo y Lola y mitad su comida habitual.', { nombre })}
                      </span>
                      <span className="hidden sm:inline">
                        {t('step11v2.mixta.desc', 'Mitad Milo y Lola y mitad su comida habitual.', { nombre })}
                      </span>
                    </p>
                  </div>
                </div>
                {dietPreviews && (
                  <div className="grid w-full grid-cols-3 gap-2 rounded-xl bg-[#FAF7F1] p-3 sm:block sm:space-y-1.5">
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.coverage', 'Ración')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">{dietPreviews.mixta.coveragePct}%</span>
                    </div>
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.grams', 'Gramos/día')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">
                        {formatNumber(dietPreviews.mixta.dailyGrams)}g
                      </span>
                    </div>
                    <div className="font-fredoka text-sm sm:flex sm:justify-between sm:gap-3">
                      <span className="block text-[11px] text-black/45 sm:text-sm">
                        {t('step11v2.card.kcal', 'Kcal/día')}
                      </span>
                      <span className="block font-bold text-[#1a1a1a]">
                        {formatNumber(dietPreviews.mixta.dailyKcal)} kcal
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </SelectableCard>
          </div>
        </div>
      </div>

      <FunnelStickyCTA
        label={
          previewLoading
            ? t('step11v2.cta_loading', 'Calculando...')
            : previewError
              ? t('step11v2.retry', 'Reintentar')
              : t('step11v2.cta_diet', 'Elegir cómo empezar →')
        }
        onClick={handleContinue}
        disabled={ctaDisabled}
        onDisabledAttempt={() => undefined}
        onBack={() => setCurrentStep(10)}
        backLabel={t('step11v2.back', 'Atrás')}
      />
    </div>
  );
}
